// Basic
import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
// Libs
import { cn } from '@/common-utils/libraries/tailwind/utils';
// Components
import Entity from '@/common-utils/components/renders/entity/Entity';
import Icon from '@/common-utils/components/renders/icon/Icon';
import PopoverHelper from '@/common-utils/components/renders/popover/PopoverHelper';
import { Button } from '@/common-utils/components/ui/button';
import { Input } from '@/common-utils/components/ui/input';
import Select from '@/common-utils/components/ui/select';
// Common
import ICONS from '@/common-utils/enums/icons';
import SIM_OFFERS_PLANS_OPTIONS from '../../../enums/SimOffersPlans';
// Lang
import { nsSimCard } from '@/app/i18n/namespaces';

/**
 *
 * @param root0
 * @param root0.addFilter
 * @param root0.removeFilter
 * @param root0.resetThisFilter
 * @param root0.setLaunchSearch
 * @returns {JSX} PlanFilter
 */
function PlanFilter({ addFilter, removeFilter, resetThisFilter, setLaunchSearch }) {

    // Constants
    const optionsUnit = [
        { name: 'Mo', value: 'MB' },
        { name: 'Go', value: 'GB' }
    ];

    // State
    const [selectedValueUnit, setSelectedValueUnit] = useState(undefined);
    const [selectedValuePlan, setSelectedValuePlan] = useState(undefined);
    const [isPlanFilterOpen, setIsPlanFilterOpen] = useState(true);
    const [inputValue, setInputValue] = useState('');
    const [isDirty, setIsDirty] = useState(false);

    // Hooks
    useEffect(() => {
        if (resetThisFilter && isDirty) {
            setInputValue('');
            setSelectedValueUnit(undefined);
            setSelectedValuePlan(undefined);
            setIsDirty(false);
        }
    }, [resetThisFilter]);

    // Functions
    const onChangeUnit = (selectedVal) => {
        setSelectedValueUnit(selectedVal);
        setIsDirty(true);
    };
    const onChangePlan = (selectedVal) => {
        setSelectedValuePlan(selectedVal);
        setIsDirty(true);
    };

    const updateFilter = () => {
        if (inputValue) {
            addFilter(
                'sim.tariffData',
                inputValue
            );
        }
        if (selectedValueUnit) {
            addFilter(
                'sim.tariffDataUnit',
                selectedValueUnit
            );
        }
        // Ignore the ALL plan filter name
        if (selectedValuePlan !== SIM_OFFERS_PLANS_OPTIONS.ALL.value) {
            addFilter(
                'sim.tariffName',
                selectedValuePlan
            );
        }
        else {
            removeFilter(['sim.tariffName']);
        }
        setIsDirty(true);
        setIsPlanFilterOpen(false);
        setLaunchSearch(true);
    };

    const onOpenChange = () => {
        setIsPlanFilterOpen(!isPlanFilterOpen);
    };

    return (
        <>
            <PopoverHelper
                isOpen={isPlanFilterOpen}
                asChild
                onOpenChange={onOpenChange}
                trigger={
                    <Button
                        variant={'outline'}
                        className={cn(
                            'pl-3 text-left font-normal min-w-60 h-10'
                        )}
                    >
                        {
                            inputValue || selectedValuePlan || selectedValueUnit ?
                                <>
                                    {inputValue || ''}
                                    {selectedValueUnit && <Entity entity={`units.${selectedValueUnit}`} namespace={nsSimCard} className={'mx-1'} />}
                                    {selectedValuePlan && <Entity entity={`filter.plan_types.${selectedValuePlan}`} namespace={nsSimCard} />}
                                </>
                                :
                                <>
                                    <Icon icon={ICONS.SEARCH}></Icon>
                                    <Icon icon={ICONS.SIM_CARDS_OFFER}></Icon>
                                </>
                        }
                    </Button>
                }
                content={
                    <>
                        <div>
                            <Entity nowrap entity={'filter.label.quantity'} namespace={nsSimCard} className={'mr-1'} />
                            <div className='flex gap-2'>
                                <Input
                                    value={inputValue}
                                    type='number'
                                    onChange={e => {
                                        setInputValue(e.target.value);
                                        setIsDirty(true);
                                    }}
                                    className='flex-1'
                                />
                                <Select
                                    value={selectedValueUnit}
                                    options={optionsUnit}
                                    onChange={onChangeUnit}
                                    className={'flex-1 items-center'}
                                    placeholder={'filter.placeholder.units'}
                                    namespace={nsSimCard}
                                />
                            </div>
                        </div>
                        <div className='mt-2'>
                            <Entity nowrap entity={'filter.label.plan'} namespace={nsSimCard} className={'mr-1'} />
                            <Select
                                value={selectedValuePlan}
                                options={Object.values(SIM_OFFERS_PLANS_OPTIONS)}
                                onChange={onChangePlan}
                                className={'min-w-48 items-center'}
                            />
                        </div>
                        <Button
                            entity='button.submit'
                            onClick={updateFilter}
                            variant='primary'
                            className={'mt-2 w-full'}
                            disabled={selectedValuePlan === undefined || selectedValueUnit === undefined || inputValue === ''}
                        />
                    </>
                }
                contentClass={'p-2'}
            />
        </>
    );
}

PlanFilter.propTypes = {
    addFilter: PropTypes.func.isRequired,
    removeFilter: PropTypes.func.isRequired,
    resetThisFilter: PropTypes.bool,
    setLaunchSearch: PropTypes.func.isRequired
};

export default PlanFilter;